import React, {
    ChangeEvent,
    ReactNode,
    ForwardedRef,
    forwardRef,
    useCallback,
    useMemo
  } from 'react';
  
  interface InputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> {
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
    variant?: 'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error';
    error?: string;
    options?: { label: string; value: string }[]; // Pour `select` uniquement
  }
  
  const inputVariantClasses = {
    primary: 'input-primary',
    secondary: 'input-secondary',
    accent: 'input-accent',
    info: 'input-info',
    success: 'input-success',
    warning: 'input-warning',
    error: 'input-error',
  };
  
  const textareaVariantClasses = {
    primary: 'textarea-primary',
    secondary: 'textarea-secondary',
    accent: 'textarea-accent',
    info: 'textarea-info',
    success: 'textarea-success',
    warning: 'textarea-warning',
    error: 'textarea-error',
  };
  
  const selectVariantClasses = {
    primary: 'select-primary',
    secondary: 'select-secondary',
    accent: 'select-accent',
    info: 'select-info',
    success: 'select-success',
    warning: 'select-warning',
    error: 'select-error',
  };
  
  export const Input = forwardRef<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    InputProps
  >(({
    type = 'text',
    placeholder = '',
    className = '',
    leftIcon = null,
    rightIcon = null,
    value,
    onChange,
    variant = 'primary',
    error,
    options = [],
    ...props
  }, ref: ForwardedRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const variantClasses = useMemo(() => {
      if (type === "select") return selectVariantClasses;
      if (type === "textarea") return textareaVariantClasses;
      return inputVariantClasses;
    }, [type]);
  
    const variantClass = useMemo(() => variant ? variantClasses[variant] : '', [variant, variantClasses]);
    const inputType = useMemo(() => {
      if (type === 'select') return 'select';
      if (type === 'textarea') return 'textarea';
      return 'input';
    }, [type]);
  
    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      if (onChange) onChange(e);
    }, [onChange]);
  
    return (
      <div className="flex flex-col mt-2">
        <label className={`${inputType} focus:outline-none flex items-center gap-2 ${className} ${variantClass}`}>
  
          {leftIcon && <span className="opacity-70">{leftIcon}</span>}
  
          {type === 'select' ? (
            <select
              className="grow focus:outline-none bg-white text-black p-2"
              value={value}
              onChange={handleChange}
              ref={ref as ForwardedRef<HTMLSelectElement>}
              {...props}
            >
              <option value="" disabled>{placeholder}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : type === 'textarea' ? (
            <textarea
              value={value}
              placeholder={placeholder}
              onChange={handleChange}
              className="grow focus:outline-none bg-white text-black p-2"
              maxLength={500}
              ref={ref as ForwardedRef<HTMLTextAreaElement>}
              {...props}
            />
          ) : (
            <input
              type={type}
              className="grow focus:outline-none bg-white text-black p-2"
              placeholder={placeholder}
              value={value}
              onChange={handleChange}
              ref={ref as ForwardedRef<HTMLInputElement>}
              {...props}
            />
          )}
  
          {rightIcon && <span className="opacity-70">{rightIcon}</span>}
        </label>
  
        {error && <span className="text-red-500 text-md mt-2">{error}</span>}
      </div>
    );
  });
  
  Input.displayName = 'Input';
